import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';

const formatDate = (value) => new Intl.DateTimeFormat('pl-PL', {
  dateStyle: 'medium',
  timeStyle: 'short',
}).format(new Date(value));

const TrashIcon = () => (
  <svg
    className="notification-delete-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v5M14 11v5" />
  </svg>
);

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingAll, setDeletingAll] = useState(false);
  const [error, setError] = useState('');

  const loadNotifications = useCallback(async () => {
    try {
      setError('');
      const data = await notificationService.getNotifications(100, false);
      setNotifications(data.notifications || []);
    } catch (err) {
      setError('Nie udało się pobrać powiadomień. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(items => items.map(item => ({ ...item, is_read: true })));
    } catch (err) {
      setError('Nie udało się oznaczyć powiadomień jako przeczytane.');
    }
  };

  const openNotification = async (notification) => {
    if (!notification.is_read) {
      try {
        await notificationService.markAsRead([notification.id]);
        setNotifications(items => items.map(item => item.id === notification.id ? { ...item, is_read: true } : item));
      } catch (err) {
        setError('Nie udało się oznaczyć powiadomienia jako przeczytane.');
        return;
      }
    }
    // Reassignment notifications are informational. This also protects older
    // notifications persisted before their backend link was removed.
    if (notification.link && notification.type !== 'consultation_reassigned') {
      navigate(notification.link);
    }
  };

  const removeNotification = async (event, id) => {
    event.stopPropagation();
    try {
      await notificationService.deleteNotification(id);
      setNotifications(items => items.filter(item => item.id !== id));
    } catch (err) {
      setError('Nie udało się usunąć powiadomienia.');
    }
  };

  const removeAllNotifications = async () => {
    const confirmed = window.confirm(
      'Czy na pewno chcesz usunąć wszystkie powiadomienia? Tej operacji nie można cofnąć.'
    );
    if (!confirmed) return;

    try {
      setDeletingAll(true);
      setError('');
      await notificationService.deleteAllNotifications();
      setNotifications([]);
    } catch (err) {
      setError('Nie udało się usunąć wszystkich powiadomień.');
    } finally {
      setDeletingAll(false);
    }
  };

  const hasUnread = notifications.some(item => !item.is_read);

  return (
    <section className="notifications-page">
      <div className="page-header notifications-page-header">
        <div>
          <h2>Powiadomienia</h2>
          <p>Twoje powiadomienia.</p>
        </div>
        {notifications.length > 0 && (
          <div className="notifications-page-actions">
            {hasUnread && (
              <button className="notifications-page-action" onClick={markAllAsRead} disabled={deletingAll}>
                Oznacz wszystkie jako przeczytane
              </button>
            )}
            <button
              className="notifications-page-action notifications-page-action--danger"
              onClick={removeAllNotifications}
              disabled={deletingAll}
            >
              <TrashIcon />
              {deletingAll ? 'Usuwanie…' : 'Usuń wszystkie'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}
      {loading ? (
        <div className="notifications-page-state">Ładowanie powiadomień…</div>
      ) : notifications.length === 0 ? (
        <div className="notifications-page-state"><span>📭</span><strong>Brak powiadomień</strong></div>
      ) : (
        <div className="notifications-page-list">
          {notifications.map(notification => (
            <article
              key={notification.id}
              className={`notifications-page-item ${notification.is_read ? '' : 'unread'} ${notification.link && notification.type !== 'consultation_reassigned' ? 'clickable' : ''}`}
              onClick={() => openNotification(notification)}
            >
              <div className="notifications-page-icon" aria-hidden="true">{notification.is_read ? '✓' : '🔔'}</div>
              <div className="notification-content">
                <h3>{notification.title}</h3>
                <p>{notification.message}</p>
                <time dateTime={notification.created_at}>{formatDate(notification.created_at)}</time>
              </div>
              <button
                type="button"
                className="notification-delete-btn"
                onClick={(event) => removeNotification(event, notification.id)}
                aria-label={`Usuń powiadomienie: ${notification.title}`}
              >
                <TrashIcon />
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default Notifications;
